// @flow
import * as React from 'react';

import sortedUniq from 'lodash/sortedUniq';

import { selectCartUUID } from '@gandi/cart';
import type { Tld } from '@gandi/domain-configuration';
import { useTranslator, type TranslatorProps } from '@gandi/translate';
import { Button, Alert, Base } from '@gandi/ui';

import { selectProductsWithDomainContactError } from 'src/Cart/DomainContacts/Wizard/modules/selectors';
import type { ProductDomainInfo } from 'src/Cart/DomainContacts/Wizard/types';
import { useAppSelector } from 'src/hooks';

import { selectShouldShowContactWizardAlert } from '../modules/selectors';

export type ContactWizardAlertProps = {
  translator: TranslatorProps,
  cartId: string,
  hide: boolean,
  productsWithContactErrors: Array<ProductDomainInfo>,
  ...
};

const localizedJoin =
  (translator: TranslatorProps) => (tldList: Array<string>) => {
    const tlds = [...tldList];
    if (tlds.length === 1) {
      return translator.__(
        '%(tld)s domains require specific domain contact information.' +
          '<br/>Add the missing information to complete your order.',
        {
          withHTML: true,
          tld: tlds[0],
        },
      );
    }

    // $FlowIgnore[incompatible-cast] tlds can't be an empty array here, so last can't be undefined
    const last = tlds.pop() as string;
    const commaSeparatedTlds = tlds.join(translator.__(', '));
    return translator.__(
      '%(tlds)s and %(last)s domains require specific domain contact information.' +
        '<br/>Add the missing information to complete your order.',
      {
        withHTML: true,
        tlds: commaSeparatedTlds,
        last,
      },
    );
  };

export const getAlertMessage = (
  translator: TranslatorProps,
  domainsInError: Array<Object>,
): any => {
  let message;

  const allDomainsHaveTld = domainsInError.every(
    domain => typeof domain.tld === 'string',
  );

  if (allDomainsHaveTld) {
    const tlds = domainsInError.map(domain => domain.tld).sort();
    const sortedUniqueTlds = sortedUniq(tlds);
    const wizardTlds: Array<Tld> = sortedUniqueTlds.map(tld =>
      `.${tld}`.toUpperCase(),
    );
    message = localizedJoin(translator)(wizardTlds);
  } else {
    message = translator.__(
      'Some domains in your cart require specific domain contact information.' +
        '<br/>Add the missing information to complete your order.',
      { withHTML: true },
    );
  }
  return message;
};

export function ContactWizardAlert(): null | React.MixedElement {
  const translator = useTranslator();
  const { __ } = translator;
  const cartId = useAppSelector(selectCartUUID);
  const show = useAppSelector(selectShouldShowContactWizardAlert);
  const productsWithContactErrors = useAppSelector(
    selectProductsWithDomainContactError,
  );

  const actionUrl = `/cart/${cartId}/configure/domain-contacts`;

  if (!show) {
    return null;
  }

  const message = getAlertMessage(translator, productsWithContactErrors);

  return (
    <Base marginBottom="base">
      <Alert
        intent="warning"
        message={message}
        buttons={
          <Button to={actionUrl} small>
            {__('Configure my domains')}
          </Button>
        }
      />
    </Base>
  );
}
